/**
 * useActiveLiveTranslationSession — picks the Live Translation transport hook
 * at module load from a RUNTIME capability check. Both hooks expose the
 * identical SessionHandle, so LiveTranslationProvider + the modal/pill stay
 * provider-agnostic.
 *
 * OTA SAFETY (option B): the Gemini interpreter needs the react-native-audio-api
 * native module, which only exists in a full native build (App/Play release) —
 * NOT in an old binary that received only an OTA JS update. So we probe the
 * native module ONCE here:
 *   • present (capable store build)  → Gemini interpreter, with automatic
 *                                       fallback to OpenAI/WebRTC if Gemini
 *                                       can't connect (useGeminiWithOpenAiFallback)
 *   • absent  (OTA / old binary)     → OpenAI path (translation still works;
 *                                       react-native-webrtc ships in every binary)
 *
 * Translation is therefore NEVER hidden — it just uses the best transport the
 * running binary can support, and even a capable build drops to WebRTC rather
 * than failing when Gemini's WS handshake can't complete on weak wifi. The
 * choice is made ONCE at module load → one stable hook call site (no
 * rules-of-hooks violation). The Gemini-capable wrapper (and its
 * react-native-audio-api import) is loaded with a conditional require so an old
 * binary never even evaluates that native module.
 */
import { TurboModuleRegistry } from "react-native";

import { useLiveTranslationSession, type SessionHandle } from "./useLiveTranslationSession";

/** True only when the native audio module is linked into the running binary. */
function isAudioApiAvailable(): boolean {
    try {
        // react-native-audio-api registers this TurboModule; `get` returns null
        // (does NOT throw) when the native side is absent.
        return TurboModuleRegistry.get("AudioAPIModule") != null;
    } catch {
        return false;
    }
}

export const LIVE_TRANSLATION_PROVIDER: "gemini" | "openai" = isAudioApiAvailable()
    ? "gemini"
    : "openai";

// Load the Gemini-capable wrapper ONLY on a capable binary, so an OTA/old
// binary never imports react-native-audio-api (avoids any native-install side
// effects). The wrapper runs Gemini as primary with an OpenAI/WebRTC fallback.
function resolveActiveHook(): () => SessionHandle {
    if (LIVE_TRANSLATION_PROVIDER === "gemini") {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const mod = require("./useGeminiWithOpenAiFallback") as {
            useGeminiWithOpenAiFallback: () => SessionHandle;
        };
        return mod.useGeminiWithOpenAiFallback;
    }
    return useLiveTranslationSession;
}

export const useActiveLiveTranslationSession: () => SessionHandle = resolveActiveHook();
